/**
 * useAdjacencyMapSyncStatus (WQ-210)
 *
 * Aggregates the three WQ-204 endpoints into one object + a health verdict.
 * 30s polling (sync interval is 5 min, so 30s is 10x finer than data change
 * rate). Plain useEffect/setInterval — matches SharePointSyncPanel's
 * pattern, no react-query dependency.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSchedulerSharePointJob,
  getSharePointConfig,
  getSyncHistory,
  SchedulerJobState,
  SharePointConfigResponse,
  SyncHistoryEntry,
} from '../services/adjacencyMapSyncService';

export type SyncHealth =
  | 'healthy'
  | 'stale'
  | 'errored'
  | 'disabled'
  | 'unknown';

export interface AdjacencyMapSyncStatus {
  health: SyncHealth;
  enabled: boolean;
  intervalMinutes: number;
  lastScheduledSync: string | null;
  lastHeartbeatLog: string | null;
  nextRunTime: string | null;
  secondsUntilNextRun: number | null;
  recentRuns: SyncHistoryEntry[];
  mostRecentRun: SyncHistoryEntry | null;
  errorCountLast10: number;
  summary: string;
}

const STALE_MULTIPLIER = 2.5; // interval × 2.5 = "expected to have run by now"
const POLL_INTERVAL_MS = 30_000;

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

function computeHealth(
  config: SharePointConfigResponse | null,
  history: SyncHistoryEntry[],
): SyncHealth {
  if (!config) return 'unknown';
  if (!config.enabled) return 'disabled';

  const mostRecent = history[0];
  if (mostRecent && (mostRecent.errors?.length ?? 0) > 0) return 'errored';
  if (mostRecent && mostRecent.status && mostRecent.status !== 'success') {
    return 'errored';
  }

  if (config.last_scheduled_sync) {
    const last = new Date(config.last_scheduled_sync).getTime();
    if (!Number.isNaN(last)) {
      const ageMs = Date.now() - last;
      const expectedMs =
        config.interval_minutes * 60_000 * STALE_MULTIPLIER;
      if (ageMs > expectedMs) return 'stale';
    }
  } else {
    // Enabled but never run yet — cold-start window. Show unknown until
    // the first tick lands.
    return 'unknown';
  }

  return 'healthy';
}

function buildSummary(
  health: SyncHealth,
  mostRecentRun: SyncHistoryEntry | null,
): string {
  if (health === 'disabled') return 'Sync disabled';
  if (health === 'unknown' && !mostRecentRun) return 'Awaiting first sync';
  if (health === 'unknown') return 'Sync status unknown';

  if (mostRecentRun) {
    const parts: string[] = [];
    if (mostRecentRun.pulled) parts.push(`${mostRecentRun.pulled} pulled`);
    if (mostRecentRun.pushed) parts.push(`${mostRecentRun.pushed} pushed`);
    if (mostRecentRun.conflicts)
      parts.push(`${mostRecentRun.conflicts} conflicts`);
    if ((mostRecentRun.errors?.length ?? 0) > 0) {
      parts.push(
        `${mostRecentRun.errors.length} error${mostRecentRun.errors.length === 1 ? '' : 's'}`,
      );
    }
    if (parts.length === 0) parts.push('no changes');
    return parts.join(', ');
  }

  return 'Awaiting first sync';
}

export interface UseAdjacencyMapSyncStatusResult {
  status: AdjacencyMapSyncStatus;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const EMPTY_STATUS: AdjacencyMapSyncStatus = {
  health: 'unknown',
  enabled: false,
  intervalMinutes: 5,
  lastScheduledSync: null,
  lastHeartbeatLog: null,
  nextRunTime: null,
  secondsUntilNextRun: null,
  recentRuns: [],
  mostRecentRun: null,
  errorCountLast10: 0,
  summary: 'Sync status unknown',
};

export function useAdjacencyMapSyncStatus(
  mapId: string | null,
  tenantId: string | null,
  authFetch: FetchFn,
): UseAdjacencyMapSyncStatusResult {
  const [config, setConfig] = useState<SharePointConfigResponse | null>(null);
  const [history, setHistory] = useState<SyncHistoryEntry[]>([]);
  const [schedulerJob, setSchedulerJob] = useState<SchedulerJobState | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guard against stale setState after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!mapId) {
      setIsLoading(false);
      return;
    }
    try {
      const [cfg, hist, job] = await Promise.all([
        getSharePointConfig(authFetch, mapId, tenantId).catch(() => null),
        getSyncHistory(authFetch, mapId, tenantId, 10).catch(() => []),
        getSchedulerSharePointJob(authFetch).catch(() => null),
      ]);
      if (!mountedRef.current) return;
      setConfig(cfg);
      setHistory(hist);
      setSchedulerJob(job);
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load sync status');
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [authFetch, mapId, tenantId]);

  useEffect(() => {
    void fetchAll();
    const id = setInterval(() => {
      void fetchAll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  const mostRecentRun = history[0] ?? null;
  const errorCountLast10 = history.reduce(
    (acc, h) => acc + (h.errors?.length ?? 0),
    0,
  );
  const health = computeHealth(config, history);

  const status: AdjacencyMapSyncStatus = config
    ? {
        health,
        enabled: config.enabled,
        intervalMinutes: config.interval_minutes,
        lastScheduledSync: config.last_scheduled_sync,
        lastHeartbeatLog: config.last_heartbeat_log,
        nextRunTime: schedulerJob?.next_run_time ?? null,
        secondsUntilNextRun: schedulerJob?.seconds_until_next_run ?? null,
        recentRuns: history,
        mostRecentRun,
        errorCountLast10,
        summary: buildSummary(health, mostRecentRun),
      }
    : {
        ...EMPTY_STATUS,
        recentRuns: history,
        mostRecentRun,
        errorCountLast10,
        nextRunTime: schedulerJob?.next_run_time ?? null,
        secondsUntilNextRun: schedulerJob?.seconds_until_next_run ?? null,
        summary: buildSummary('unknown', mostRecentRun),
      };

  return {
    status,
    isLoading,
    error,
    refresh: () => {
      void fetchAll();
    },
  };
}

// Exported for tests
export const __private = { computeHealth, buildSummary, STALE_MULTIPLIER };
